import { useMemo } from "react";
import { Link } from "react-router";
import { Notice, StatTile } from "../../design/components";
import { SLOW_POLL, usePortalDocuments, usePortalMe, usePortalSummary } from "../../api/portal";
import { relative } from "../../lib/format";
import { StageStrip } from "../documents/StageStrip";
import { AttentionList } from "./AttentionList";
import { HelpdeskLink } from "./HelpdeskLink";
import { DocumentCells, portalQueue } from "./PortalDocuments";
import styles from "./portal.module.css";

const RECENT = 20;
const DIRECTION_LABEL = { out: "Outbound", in: "Inbound" } as const;

export function Overview() {
  const me = usePortalMe();
  const summary = usePortalSummary();
  const recent = usePortalDocuments({ limit: RECENT }, SLOW_POLL);
  const rows = useMemo(() => (recent.data?.pages[0]?.items ?? []).slice(0, RECENT), [recent.data]);
  const data = summary.data;

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">Overview</h1>
          <p className={styles.lede}>What your company sent and received, and anything that needs a hand.</p>
        </div>
      </div>

      {summary.isError ? (
        <Notice kind="fail">
          <div className="row">
            <span className="grow">The summary could not be loaded.</span>
            <button type="button" className="btn btn-sm" onClick={() => void summary.refetch()}>
              Retry
            </button>
          </div>
        </Notice>
      ) : data ? (
        <>
          <div className="grid g4">
            <StatTile label="Sent today" value={data.sentToday} meta="Outbound documents" />
            <StatTile label="In flight" value={data.inFlight} meta="Before archive" />
            <StatTile
              label="Needs attention"
              value={data.needsAttention}
              meta="Stopped or held"
              {...(data.needsAttention > 0 ? { tone: "var(--fail)" } : {})}
            />
            <StatTile
              label="Last document"
              value={data.lastDocumentAt ? relative(data.lastDocumentAt) : "—"}
              meta={data.lastDocumentAt ? "Most recent activity" : "No documents yet"}
            />
          </div>
          <StageStrip counts={data.stageCountsToday} linkTo={portalQueue} label="Stages today" />
        </>
      ) : null}

      <div className={styles.stack}>
        <AttentionList />

        <section className="card card-tight" aria-labelledby="recent-title">
          <div className="card-head">
            <h2 id="recent-title" className="grow">
              Recent documents
            </h2>
            <Link className="link" to="/portal/documents">
              See all documents
            </Link>
          </div>
          {recent.isError ? (
            <div className="card-body">
              <Notice kind="fail">The recent documents could not be loaded.</Notice>
            </div>
          ) : rows.length === 0 && !recent.isLoading ? (
            <div className="card-body">
              <Notice kind="info">No documents yet for your company</Notice>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Direction</th>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Counterparty</th>
                    <th>Stage</th>
                    <th>Status</th>
                    <th className="r">Value</th>
                    <th className="nowrap">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="nowrap">{DIRECTION_LABEL[row.direction]}</td>
                      <DocumentCells row={row} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <p className={styles.footer}>
        <span>Need help with a document?</span>{" "}
        <HelpdeskLink email={me.data?.helpdeskEmail ?? null} />
      </p>
    </>
  );
}
